import React, { useState, useEffect } from 'react';
import { Clock, Tag } from 'lucide-react';
import { usePricingSettings, formatDeadline, formatPrice, isDiscountActive } from '../hooks/useDiscountDeadline';

// Countdown timer that counts down to the configurable discount deadline
const CountdownTimer = ({ targetDate, subtle = false, darkBg = false }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate) - new Date();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
        // Trigger pulse animation on each second
        setPulse(true);
        setTimeout(() => setPulse(false), 300);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  // Animated subtle variant - clean inline display with ticking seconds
  if (subtle) {
    return (
      <div className={`flex items-center gap-2 text-sm ${darkBg ? 'text-white/80' : 'text-gray-600'}`}>
        <div className={`flex items-center gap-1 transition-transform duration-300 ${pulse ? 'scale-105' : 'scale-100'}`}>
          <Clock className={`h-4 w-4 ${darkBg ? 'text-blue-300' : 'text-blue-500'}`} />
        </div>
        <div className="flex items-center gap-1 font-mono">
          <span className={`font-semibold tabular-nums ${darkBg ? 'text-white' : 'text-gray-900'}`}>
            {timeLeft.days}
          </span>
          <span className="text-xs opacity-70">d</span>
          <span className={`font-semibold tabular-nums ${darkBg ? 'text-white' : 'text-gray-900'}`}>
            {String(timeLeft.hours).padStart(2, '0')}
          </span>
          <span className="text-xs opacity-70">u</span>
          <span className={`font-semibold tabular-nums ${darkBg ? 'text-white' : 'text-gray-900'}`}>
            {String(timeLeft.minutes).padStart(2, '0')}
          </span>
          <span className="text-xs opacity-70">m</span>
          <span className={`font-semibold tabular-nums transition-all duration-300 ${pulse ? 'opacity-100' : 'opacity-70'} ${darkBg ? 'text-blue-300' : 'text-blue-500'}`}>
            {String(timeLeft.seconds).padStart(2, '0')}
          </span>
          <span className="text-xs opacity-70">s</span>
        </div>
      </div>
    );
  }

  // Full animated variant with boxes
  return (
    <div className="flex items-center gap-3">
      <Clock className={`h-5 w-5 text-blue-500 transition-transform duration-300 ${pulse ? 'rotate-12' : 'rotate-0'}`} />
      <div className="flex gap-2">
        {[
          { value: timeLeft.days, label: 'd' },
          { value: timeLeft.hours, label: 'u' },
          { value: timeLeft.minutes, label: 'm' },
          { value: timeLeft.seconds, label: 's' }
        ].map((item, idx) => (
          <div 
            key={item.label}
            className={`bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg px-2 py-1.5 text-center min-w-[44px] shadow-sm transition-all duration-300 ${idx === 3 && pulse ? 'scale-110 shadow-md' : 'scale-100'}`}
          >
            <span className="font-bold text-gray-900 tabular-nums text-lg">
              {String(item.value).padStart(2, '0')}
            </span>
            <span className="text-xs text-gray-500 ml-0.5">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Workshop price display with anchoring, savings, and urgency
const WorkshopPriceDisplay = ({ 
  language = 'nl', 
  variant = 'default', // 'default', 'compact', 'badge'
  showCountdown = true,
  className = ''
}) => {
  const { discount_deadline: deadlineStr, original_price: originalPrice, current_price: currentPrice } = usePricingSettings();
  const savings = originalPrice - currentPrice;
  const discountPct = Math.round((savings / originalPrice) * 100);
  const deadline = new Date(`${deadlineStr}T23:59:59`);
  const discountActive = isDiscountActive(deadlineStr);
  
  const texts = {
    nl: {
      was: 'Was',
      now: 'Nu',
      save: 'Bespaar',
      intro: 'Introductieprijs',
      validUntil: `Geldig t/m ${formatDeadline(deadlineStr, 'nl')}`,
      exclVat: 'excl. BTW',
      limited: 'Tijdelijk aanbod'
    },
    en: {
      was: 'Was',
      now: 'Now',
      save: 'Save',
      intro: 'Introductory price',
      validUntil: `Valid until ${formatDeadline(deadlineStr, 'en')}`,
      exclVat: 'excl. VAT',
      limited: 'Limited time offer'
    }
  };
  
  const t = texts[language] || texts.nl;

  // Compact badge variant (for headers, cards)
  if (variant === 'badge') {
    if (!discountActive) {
      return (
        <div className={`inline-flex items-center gap-2 ${className}`} data-testid="price-badge-normal">
          <span className="bg-blue-500 text-white font-bold px-3 py-1 rounded-full">
            {formatPrice(originalPrice, language)}
          </span>
        </div>
      );
    }
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        <span className="text-gray-400 line-through text-sm">{formatPrice(originalPrice, language)}</span>
        <span className="bg-blue-500 text-white font-bold px-3 py-1 rounded-full">
          {formatPrice(currentPrice, language)}
        </span>
        <span className="text-blue-500 text-xs font-medium">-{discountPct}%</span>
      </div>
    );
  }

  // Compact variant (for inline use)
  if (variant === 'compact') {
    if (!discountActive) {
      return (
        <div className={`flex flex-col items-center gap-1 ${className}`} data-testid="price-compact-normal">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">{formatPrice(originalPrice, language)}</span>
            <span className="text-blue-200 text-sm font-normal">{t.exclVat}</span>
          </div>
        </div>
      );
    }
    return (
      <div className={`flex flex-col items-center gap-1 ${className}`}>
        <div className="flex items-center gap-2">
          <span className="text-gray-400 line-through">{formatPrice(originalPrice, language)}</span>
          <span className="text-2xl font-bold text-gray-900">{formatPrice(currentPrice, language)}</span>
          <span className="text-blue-200 text-sm font-normal">{t.exclVat}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-green-600">
          <Tag className="h-3 w-3" />
          <span>{t.save} €{savings.toLocaleString('nl-NL')}</span>
          <span className="text-gray-400">•</span>
          <span className="text-blue-600">{t.validUntil}</span>
        </div>
        {showCountdown && (
          <div className="mt-1">
            <CountdownTimer targetDate={deadline} subtle />
          </div>
        )}
      </div>
    );
  }

  // Default full variant
  if (!discountActive) {
    return (
      <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 ${className}`} data-testid="price-default-normal">
        <div className="flex items-baseline gap-3">
          <span className="text-3xl font-bold text-gray-900">{formatPrice(originalPrice, language)}</span>
          <span className="text-gray-500 text-sm">{t.exclVat}</span>
        </div>
      </div>
    );
  }
  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 ${className}`}>
      {/* Limited time badge */}
      <div className="flex items-center gap-2 mb-3">
        <span className="bg-blue-500 text-white text-xs font-semibold px-2 py-1 rounded-full uppercase tracking-wide">
          {t.intro}
        </span>
        <span className="text-blue-600 text-xs font-medium">-{discountPct}%</span>
      </div>
      
      {/* Price display */}
      <div className="flex items-baseline gap-3 mb-2">
        <span className="text-gray-400 line-through text-xl">{formatPrice(originalPrice, language)}</span>
        <span className="text-3xl font-bold text-gray-900">{formatPrice(currentPrice, language)}</span>
        <span className="text-gray-500 text-sm">{t.exclVat}</span>
      </div>
      
      {/* Savings callout */}
      <div className="flex items-center gap-2 text-green-600 text-sm font-medium mb-3">
        <Tag className="h-4 w-4" />
        <span>{t.save} €{savings.toLocaleString('nl-NL')}</span>
      </div>
      
      {/* Deadline and countdown */}
      <div className="flex items-center justify-between pt-3 border-t border-blue-200">
        <span className="text-blue-700 text-sm font-medium">
          ⏰ {t.validUntil}
        </span>
        {showCountdown && (
          <CountdownTimer targetDate={deadline} subtle />
        )}
      </div>
    </div>
  );
};

export { WorkshopPriceDisplay, CountdownTimer };
export default WorkshopPriceDisplay;
